/**
 * Job List API Contracts
 *
 * ts-rest contract definitions for the paginated job list endpoints used by works-lite.
 *
 * Endpoints :
 *   GET /jobs/job-list          — base filters
 *   GET /jobs/job-list-extended — extended filters (status, assignee, date-range, sort)
 * Auth      : Required (standard bearer token via middleware)
 * Response  : Paginated list of jobs with total count
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/**
 * Zod schema for a single job list item
 */
export const jobListItemSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  job_category_id: z.number().nullable(),
  status: z.number().nullable(),
  priority: z.string().nullable(),
  priority_id: z.number().nullable(),
  priority_name: z.string().nullable(),
  address: z.string().nullable(),
  req_completion_date: z.string().nullable(),
  created_time: z.string().nullable(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  work_order_status_id: z.number().nullable(),
  work_order_status: z.string().nullable(),
  permit_required: z.boolean(),
  reinstatement_required: z.boolean(),
  permit_status_id: z.number().nullable(),
  permit_status_name: z.string().nullable(),
  job_category_name: z.string().nullable(),
  mir_pending_direction: z.enum(['inbound', 'outbound']).nullable(),
  /**
   * Display-ready "Response Received" signal for the job list. True when a
   * more-info reply / approved duration challenge has been received AND no MIR
   * request is currently pending — any outstanding request (inbound or
   * outbound) supersedes the prior response so the list does not show a stale
   * badge (WRK-5508), matching the detail page precedence. Drives the list
   * badge only; the detail page derives its own status.
   */
  has_response: z.boolean(),
  /** True when the job is PSR-flagged (psr.fieldIdentified or psr.iid). */
  is_vulnerable: z.boolean(),
  external_id: z.string().nullable(),
  duration: z.number().nullable(),
  duration_unit: z.enum(['minutes', 'hours', 'days']).nullable(),
});

/**
 * Zod schema for the paginated job list response
 */
export const jobListResponseSchema = z.object({
  data: z.array(jobListItemSchema),
  total: z.number(),
  limit: z.number(),
  skip: z.number(),
});

/**
 * Zod schema for query parameters
 */
const toArray = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (val) => (Array.isArray(val) ? val : val != null ? [val] : undefined),
    z.array(schema),
  );

const fromCommaSeparated = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((val) => {
    if (val == null) return undefined;
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val.split(',').filter(Boolean);
    return [val];
  }, z.array(schema));

/**
 * Boolean filter from a URL query string. Query params arrive as strings, so
 * z.coerce.boolean() is wrong here (it treats 'false' as true). Maps 'true'/'1'
 * to true, 'false'/'0' to false, and anything else (incl. absent) to undefined.
 */
const booleanFromQuery = z.preprocess((val) => {
  if (typeof val === 'boolean') return val;
  if (val === 'true' || val === '1') return true;
  if (val === 'false' || val === '0') return false;
  return undefined;
}, z.boolean().optional());

export const jobListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
  priority_id: fromCommaSeparated(z.coerce.number().int()).optional(),
  job_category_id: toArray(z.coerce.number().int()).optional(),
  job_category_name: toArray(z.string()).optional(),
  work_order_status_id: toArray(z.coerce.number().int()).optional(),
  work_order_status_name: toArray(z.string()).optional(),
  search_term: z.string().optional(),
  mir_pending_direction: z.enum(['inbound', 'outbound', 'none']).optional(),
  is_vulnerable: booleanFromQuery,
});

export const jobListExtendedQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  skip: z.coerce.number().int().min(0).default(0),
  // The frontend joins multiple selected priorities into one comma-separated
  // value (`priority_id=1,2`), so it must be split back out — matching the basic
  // jobListQuerySchema. `toArray` here would wrap '1,2' into ['1,2'] → NaN and
  // silently return no results when more than one priority is selected (WRK-5304).
  priority_id: fromCommaSeparated(z.coerce.number().int()).optional(),
  job_category_id: toArray(z.coerce.number().int()).optional(),
  job_category_name: toArray(z.string()).optional(),
  work_order_status_id: toArray(z.coerce.number().int()).optional(),
  work_order_status_name: toArray(z.string()).optional(),
  status: toArray(z.coerce.number().int()).optional(),
  assignee_user_id: toArray(z.coerce.number().int()).optional(),
  assignee_team_id: toArray(z.coerce.number().int()).optional(),
  created_from: z.coerce.date().optional(),
  created_to: z.coerce.date().optional(),
  updated_from: z.coerce.date().optional(),
  updated_to: z.coerce.date().optional(),
  due_from: z.coerce.date().optional(),
  due_to: z.coerce.date().optional(),
  search_term: z.string().optional(),
  mir_pending_direction: z.enum(['inbound', 'outbound', 'none']).optional(),
  is_vulnerable: booleanFromQuery,
  sort_by: z
    .enum([
      'id',
      'name',
      'priority_id',
      'status',
      'work_order_status_id',
      'job_category_id',
      'req_completion_date',
      'created_time',
      'updated_time',
      'start_date',
      'end_date',
    ])
    .optional(),
  sort_order: z.enum(['asc', 'desc']).optional(),
});

/**
 * TypeScript types inferred from Zod schemas
 */
export type JobListItem = z.infer<typeof jobListItemSchema>;
export type JobListResponse = z.infer<typeof jobListResponseSchema>;
export type JobListQuery = z.infer<typeof jobListQuerySchema>;
export type JobListExtendedQuery = z.infer<typeof jobListExtendedQuerySchema>;

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

export const jobListContract = c.router({
  getJobList: {
    method: 'GET',
    path: '/',
    query: jobListQuerySchema,
    responses: {
      200: jobListResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a paginated list of active jobs',
    description:
      'Returns jobs where is_delete = false, ordered by created_time DESC. Supports limit/skip pagination.',
  },
});

export const jobListExtendedContract = c.router({
  getJobListExtended: {
    method: 'GET',
    path: '/',
    query: jobListExtendedQuerySchema,
    responses: {
      200: jobListResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a paginated list of active jobs with extended filters',
    description:
      'Returns jobs where is_delete = false. Supports status, assignee (user/team), date-range, sort and expanded search filters.',
  },
});

export const jobTabCountsResponseSchema = z.object({
  counts: z.object({
    all: z.number(),
    'new-orders': z.number(),
    accepted: z.number(),
    rejected: z.number(),
    'more-info-requested': z.number(),
    'ready-for-validation': z.number(),
    validated: z.number(),
  }),
});

export type JobTabCountsResponse = z.infer<typeof jobTabCountsResponseSchema>;

export const jobTabCountsContract = c.router({
  getJobTabCounts: {
    method: 'GET',
    path: '/',
    responses: {
      200: jobTabCountsResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get job counts grouped by tab/status',
    description:
      'Returns a single aggregated count per tab category. Replaces N+1 individual count queries.',
  },
});
